import { useEffect, useMemo, useRef, useState } from 'react';
import { FontIcon, Panel, PanelType, PrimaryButton } from '@fluentui/react';
import {
  Button,
  FluentProvider,
  webLightTheme,
} from '@fluentui/react-components';
import { ArrowDownRegular, ArrowUpRegular } from '@fluentui/react-icons';
import { useBoolean } from '@fluentui/react-hooks';
import { useNavigate } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  PagedDataGrid,
  type DataGridColumn,
  type ICommandBarItem,
  type IPagedDataGridRefType,
} from '@moko/ui';
import ConfirmDialog from '../../common/ConfirmDialog';
import { appRoles } from '../../authConfig';
import { profileDataAtom } from '../../atoms/authAtoms';
import {
  errorMessageAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import {
  deletePricingItem,
  getPricingItems,
  getTimeServices,
  IPricingItem,
  orderPricingItems,
} from '../../services/pricingService';
import {
  calculationMethodLabel,
  chargeRoleLabel,
  percentageBasisLabel,
} from './pricingItemBody';

const yesNo = (value: boolean) => (value ? 'Yes' : 'No');

const itemColumns = (
  timeServiceNames: Record<number, string>
): DataGridColumn<IPricingItem>[] => [
  { key: 'code', name: 'Code', minWidth: 130, width: 150, isSortable: true },
  { key: 'name', name: 'Name', minWidth: 100, width: 140, isSortable: true },
  { key: 'type', name: 'Type', minWidth: 60, width: 80, isSortable: true },
  {
    key: 'calculationMethod',
    name: 'Calculation',
    minWidth: 70,
    width: 90,
    value: (item) => calculationMethodLabel(item.calculationMethod),
  },
  {
    key: 'chargeRole',
    name: 'Charge role',
    minWidth: 100,
    width: 120,
    value: (item) => chargeRoleLabel(item.chargeRole),
  },
  {
    key: 'percentageBasis',
    name: 'Percentage basis',
    minWidth: 60,
    width: 100,
    value: (item) => percentageBasisLabel(item.percentageBasis),
  },
  {
    key: 'isPercentageBaseEligible',
    name: 'Surcharge base',
    minWidth: 80,
    width: 100,
    value: (item) =>
      item.isPercentageBaseEligible == null
        ? ''
        : yesNo(item.isPercentageBaseEligible),
  },
  {
    key: 'timeServiceId',
    name: 'Time service',
    minWidth: 110,
    width: 140,
    value: (item) =>
      item.timeServiceId == null
        ? ''
        : timeServiceNames[item.timeServiceId] ?? '',
  },
  {
    key: 'quantityType',
    name: 'Quantity type',
    minWidth: 80,
    width: 120,
    isSortable: true,
  },
  {
    key: 'exclusivityGroup',
    name: 'Exclusivity group',
    minWidth: 120,
    width: 140,
    isSortable: true,
  },
  {
    key: 'isRequired',
    name: 'Required',
    minWidth: 60,
    width: 80,
    value: (item) => yesNo(item.isRequired),
  },
];

const PricingItems = () => {
  const navigate = useNavigate();
  const profileData = useAtomValue(profileDataAtom);
  const isAdmin = profileData.roles.includes(appRoles.Admin);
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const itemsRef = useRef<IPagedDataGridRefType>(null);
  const [selectedItem, setSelectedItem] = useState<IPricingItem>();
  const [
    isDeleteItemOpen,
    { setTrue: showDeleteItem, setFalse: hideDeleteItem },
  ] = useBoolean(false);
  const [isReorderOpen, { setTrue: showReorder, setFalse: hideReorder }] =
    useBoolean(false);
  const [orderItems, setOrderItems] = useState<IPricingItem[]>([]);
  const [orderSaving, setOrderSaving] = useState(false);
  const [timeServiceNames, setTimeServiceNames] = useState<
    Record<number, string>
  >({});

  useEffect(() => {
    const abortController = new AbortController();
    getTimeServices(abortController, 500, 1)
      .then((page) => {
        const names: Record<number, string> = {};
        page.items.forEach((service) => {
          names[service.id] = service.name || service.code;
        });
        setTimeServiceNames(names);
      })
      .catch(() => undefined);
    return () => abortController.abort();
  }, []);

  const itemCommands: ICommandBarItem[] = useMemo(
    () => [
      {
        key: 'newItem',
        text: 'New',
        icon: <FontIcon iconName="Add" />,
        onClick: () => {
          void navigate('/newPricingItem');
        },
        disabled: !isAdmin,
      },
      {
        key: 'editItem',
        text: 'Edit',
        icon: <FontIcon iconName="Edit" />,
        onClick: () => {
          if (selectedItem)
            void navigate(`/editPricingItem?id=${selectedItem.id}`);
        },
        disabled: !isAdmin || !selectedItem,
      },
      {
        key: 'reorderItems',
        text: 'Reorder',
        icon: <FontIcon iconName="Sort" />,
        onClick: () => {
          const abortController = new AbortController();
          getPricingItems(abortController, 500, 1)
            .then((page) => {
              setOrderItems(
                [...page.items].sort((a, b) => a.sortOrder - b.sortOrder)
              );
              showReorder();
            })
            .catch((error: { message?: string }) => {
              setErrorMessage(error.message ?? 'Failed to load pricing items.');
            });
        },
        disabled: !isAdmin,
      },
      {
        key: 'deleteItem',
        text: 'Delete',
        icon: <FontIcon iconName="Delete" />,
        onClick: () => showDeleteItem(),
        disabled: !isAdmin || !selectedItem,
      },
    ],
    [
      isAdmin,
      navigate,
      selectedItem,
      showDeleteItem,
      setErrorMessage,
      showReorder,
    ]
  );

  return (
    <>
      <PagedDataGrid<IPricingItem>
        ref={itemsRef}
        columns={itemColumns(timeServiceNames)}
        getAction={getPricingItems}
        commandBarItems={itemCommands}
        selectionMode="single"
        onSelectionChange={setSelectedItem}
      />
      <Panel
        isOpen={isReorderOpen}
        onDismiss={hideReorder}
        type={PanelType.medium}
        headerText="Reorder pricing items"
        isFooterAtBottom
        onRenderFooterContent={() => (
          <PrimaryButton
            text="Save"
            disabled={orderSaving || orderItems.length === 0}
            onClick={() => {
              const abortController = new AbortController();
              setOrderSaving(true);
              orderPricingItems(
                abortController,
                orderItems.map((item) => item.id)
              )
                .then(() => {
                  hideReorder();
                  itemsRef.current?.refresh();
                  setSuccessMessage('Pricing item order saved.');
                })
                .catch((error: { message?: string }) => {
                  setErrorMessage(error.message ?? 'Failed to save order.');
                })
                .finally(() => setOrderSaving(false));
            }}
          />
        )}
      >
        <FluentProvider theme={webLightTheme}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              marginTop: 16,
            }}
          >
            {orderItems.map((item, index) => (
              <div
                key={item.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  padding: '8px 12px',
                  border: '1px solid #e1dfdd',
                  borderRadius: 4,
                }}
              >
                <span>
                  {item.code} {item.name}
                </span>
                <span style={{ display: 'flex', gap: 4 }}>
                  <Button
                    appearance="subtle"
                    icon={<ArrowUpRegular />}
                    disabled={index === 0}
                    aria-label="Move up"
                    onClick={() =>
                      setOrderItems((rows) => {
                        if (index === 0) return rows;
                        const next = [...rows];
                        const [row] = next.splice(index, 1);
                        next.splice(index - 1, 0, row);
                        return next;
                      })
                    }
                  />
                  <Button
                    appearance="subtle"
                    icon={<ArrowDownRegular />}
                    disabled={index === orderItems.length - 1}
                    aria-label="Move down"
                    onClick={() =>
                      setOrderItems((rows) => {
                        if (index === rows.length - 1) return rows;
                        const next = [...rows];
                        const [row] = next.splice(index, 1);
                        next.splice(index + 1, 0, row);
                        return next;
                      })
                    }
                  />
                </span>
              </div>
            ))}
          </div>
        </FluentProvider>
      </Panel>
      <ConfirmDialog
        isModalOpen={isDeleteItemOpen}
        hideModal={hideDeleteItem}
        message={`Pricing item ${
          selectedItem?.code ?? ''
        } would be deleted, are you sure?`}
        onYesClick={() => {
          hideDeleteItem();
          if (!selectedItem) return;
          const abortController = new AbortController();
          deletePricingItem(abortController, selectedItem.id)
            .then(() => {
              itemsRef.current?.refresh();
              setSuccessMessage(`Pricing item ${selectedItem.code} deleted.`);
              setSelectedItem(undefined);
            })
            .catch((error: { message?: string }) => {
              setErrorMessage(
                error.message ?? 'Failed to delete pricing item.'
              );
            });
        }}
      />
    </>
  );
};

export default PricingItems;
